import { useQuery } from "@tanstack/react-query";
import type { EditorialPolicy, InsightArticle, InsightAuthor, InsightCategory, InsightsResponse, PressStory } from "../types/insights";
import { editorialCategoryNameFromSlug } from "../../../shared/editorialSeo";
import { useSSRData } from "../contexts/SSRDataContext";

const get = async <T,>(path: string): Promise<T> => {
  const response = await fetch(`/api/editorial${path}`);
  if (!response.ok) throw new Error(`Editorial request failed (${response.status})`);
  return response.json();
};

/** API contracts: GET /api/editorial returns {articles,categories,authors}; singular endpoints return the entity. */
export function useInsights() {
  const { seoData } = useSSRData();
  const initialData = seoData?.type === "editorial-index"
    ? { articles: seoData.articles || [], categories: seoData.categories || [], authors: seoData.authors || [] }
    : undefined;
  return useQuery<InsightsResponse>({ queryKey: ["editorial", "insights"], queryFn: () => get("/"), initialData, staleTime: 5 * 60_000 });
}
export function useInsight(slug?: string) {
  const { seoData } = useSSRData();
  // GET /:slug currently returns { article, author, reviewer }; normalize it here
  // so page components stay stable as the editorial service adds envelope fields.
  return useQuery<InsightArticle>({ queryKey: ["editorial", "article", slug], queryFn: async () => {
    const response = await get<InsightArticle | { article: InsightArticle; author?: InsightAuthor; reviewer?: InsightAuthor }>(`/${encodeURIComponent(slug!)}`);
    if ("article" in response) return { ...response.article, author: response.author, reviewer: response.reviewer };
    return response;
  }, enabled: Boolean(slug), initialData: seoData?.type === "editorial-article" && seoData.article?.slug === slug ? seoData.article : undefined, staleTime: 5 * 60_000 });
}
export function useInsightCategory(slug?: string) {
  const { seoData } = useSSRData();
  return useQuery<InsightCategory & { articles: InsightArticle[] }>({ queryKey: ["editorial", "category", slug], queryFn: async () => {
    return get<InsightCategory & { articles: InsightArticle[] }>(`/categories/${encodeURIComponent(slug!)}`);
  }, enabled: Boolean(slug), initialData: seoData?.type === "editorial-category" && seoData.slug === slug ? seoData : undefined, staleTime: 5 * 60_000 });
}
export function useInsightAuthor(slug?: string) {
  const { seoData } = useSSRData();
  return useQuery<InsightAuthor & { articles: InsightArticle[] }>({
    queryKey: ["editorial", "author", slug],
    queryFn: async () => {
      const response = await get<InsightAuthorResponse>(`/authors/${encodeURIComponent(slug!)}`);
      return normalizeInsightAuthorResponse(response);
    },
    enabled: Boolean(slug),
    initialData: seoData?.type === "editorial-author" && seoData.slug === slug ? seoData : undefined,
    staleTime: 5 * 60_000,
  });
}
export function useEditorialPolicy() {
  const { seoData } = useSSRData();
  return useQuery<EditorialPolicy>({ queryKey: ["editorial", "policy"], queryFn: () => get("/policy"), initialData: seoData?.type === "editorial-policy" ? seoData.policy : undefined, staleTime: 30 * 60_000 });
}
export function usePressStories() {
  const { seoData } = useSSRData();
  return useQuery<{ stories: PressStory[] }>({
    queryKey: ["editorial", "press"],
    queryFn: () => get("/press"),
    initialData: seoData?.type === "editorial-press" && Array.isArray(seoData.stories)
      ? { stories: seoData.stories }
      : undefined,
    staleTime: 5 * 60_000,
  });
}

/** Current backend contract is { author, articles }; keep author metadata attached
 * to the compact article rows returned by the author endpoint for card rendering. */
export type InsightAuthorResponse = {
  author: InsightAuthor;
  articles: Array<InsightArticle & { author?: InsightAuthor }>;
};

export function normalizeInsightAuthorResponse(response: InsightAuthorResponse): InsightAuthor & { articles: InsightArticle[] } {
  return {
    ...response.author,
    articles: response.articles.map(article => ({ ...article, author: article.author || response.author })),
  };
}

export const categoryPath = (category: InsightArticle["category"] | null | undefined) => {
  const value = typeof category === "string" ? category : category?.slug || "guides";
  return value.toLowerCase().replace(/_/g, "-").replace(/\s+/g, "-");
};
export const categoryName = editorialCategoryNameFromSlug;